import { frameErrorSubscriber } from "../kernels/connector/channels/frameErrorChannel";
import {
  jupyterPollSubscriber,
  JupyterPollType,
} from "../kernels/connector/channels/jupyterPollChannel";
import { sessionStatePublisher } from "../kernels/connector/channels/sessionStateChannel";
import { websocketDisconnectionSubscriber } from "../kernels/connector/channels/websocketDisconnectionChannel";
import { getOrCreateKaggleFrameNode } from "../kernels/connector/extensions/utils/dom";
import { init } from "../kernels/init";
import { reportLifecycleChange } from "../kernels/redux/actions/lifecycle";
import { getSessionState } from "../kernels/redux/selectors/session";
import { getUserId } from "../kernels/redux/selectors/user";
import { getStore } from "../kernels/redux/store/appStore";
import { SessionState } from "../kernels/redux/types/SessionState";
import { FrameErrorInformation } from "../kernels/types/FrameErrorInformation";
import { LifecycleStage } from "../kernels/types/LifecycleLatencyStats";
import { Dimensions, FrameClient } from "../kernels/types/services/FrameClient";
import { createDispose } from "../kernels/utils/createDispose";
import { getServices } from "./getServices";

// This is intentionally not tied to an instance of FrameClient.
let currentJupyterFrame: HTMLIFrameElement;

const getContentWindow = () => {
  const contentWindow = currentJupyterFrame.contentWindow;
  if (contentWindow === null) {
    throw new Error("Attempt to access frame contentWindow before it exists.");
  }
  return contentWindow;
};

export const createFrameClient = (): FrameClient => {
  /** Gets whether the frame element currently exists. */
  const hasFrame = () => !!currentJupyterFrame;

  /** Gets the current frame element or throws if it doesn't exist. */
  const ensureFrame = () => {
    const frame = currentJupyterFrame;
    if (!frame) {
      throw new Error("Attempt to access frame before it exists.");
    }
    return frame!;
  };

  const publishSessionState = (sessionState: SessionState) => {
    const contentWindow = getContentWindow();
    const publish = sessionStatePublisher(contentWindow);
    publish(sessionState);
  };

  /**
   * Listens for frame focus and notifies callbacks when detected.
   *
   * Since frame is sandboxed we can't use its event listeners directly. Instead, we
   * emulate the onblur behavior by monitoring whether the frame is hovered when a window
   * blur event occurs.
   */
  const listenFrameFocus = (frame: HTMLIFrameElement) => {
    if (frame.parentElement === null) {
      throw new Error("Frame focus subscriber requires frame to be in DOM.");
    }

    // Since frame is sandboxed we can't use its events directly.
    const containerElement = frame.parentElement;

    let isFrameHovered = false;

    const handleFrameMouseOver = () => {
      isFrameHovered = true;
    };

    const handleFrameMouseOut = () => {
      isFrameHovered = false;
    };

    const handleFrameFocus = () => {
      if (isFrameHovered) {
        for (const subscriber of focusSubscribers) {
          subscriber();
        }
      }
    };

    containerElement.addEventListener("mouseover", handleFrameMouseOver);
    containerElement.addEventListener("mouseout", handleFrameMouseOut);
    window.addEventListener("blur", handleFrameFocus);

    // Return unsubscription function to clean up subscriptions when iframe disposed.
    return () => {
      if (containerElement) {
        containerElement.removeEventListener("mouseover", handleFrameMouseOver);
        containerElement.removeEventListener("mouseout", handleFrameMouseOut);
      }
      window.removeEventListener("blur", handleFrameFocus);
    };
  };

  let dimensionsMemo: Dimensions = {
    top: 0,
    width: 0,
    height: 0,
  };

  const createJupyterNotebookFrame = () => {
    const jupyterFrame = document.createElement("iframe");
    // These styles were copied from original React-based jupyter frame.
    jupyterFrame.style.position = "fixed";
    jupyterFrame.style.border = "0";
    jupyterFrame.style.boxSizing = "border-box";
    jupyterFrame.style.flex = "1";
    jupyterFrame.style.top = `${dimensionsMemo.top}px`;
    jupyterFrame.style.height = `${dimensionsMemo.height}px`;
    jupyterFrame.style.width = `${dimensionsMemo.width}px`;
    jupyterFrame.style.willChange = "width, height";
    jupyterFrame.style.visibility = "hidden";

    const kaggleFrameNode = getOrCreateKaggleFrameNode();
    kaggleFrameNode.appendChild(jupyterFrame);
    return jupyterFrame;
  };

  /** Tracks subscriptions for frame focus notification. */
  const focusSubscribers: (() => void)[] = [];

  return {
    async createJupyterFrame(sessionlessFn = null) {
      if (currentJupyterFrame) {
        return;
      }

      // Use bulk disposer util to clean up anything that should stop with iframe removal.
      const dispose = createDispose();

      const jupyterFrame = sessionlessFn
        ? await sessionlessFn()
        : createJupyterNotebookFrame();

      // Start listening for frame focus and stop when frame disposed.
      dispose.add(listenFrameFocus(jupyterFrame));

      // This function primarily sets up IPCs, would be nice to remove if possible.
      const store = getStore();
      init(getServices())(store);

      // This poll subscriber used to be added on load, but I don't think there's any harm in doing
      // it here.
      dispose.add(
        jupyterPollSubscriber()(pollType => {
          switch (pollType) {
            case JupyterPollType.FETCH:
              const sessionState = getSessionState(store.getState());
              publishSessionState(sessionState);
              break;
          }
        })
      );

      // When the user's cell throws an error, we log it.
      // https://goto.google.com/kernels-snippets-metrics
      dispose.add(
        frameErrorSubscriber()((error: FrameErrorInformation) => {
          const loggedError = {
            ...error,
            userId: getUserId(getStore().getState()),
            kernelUrl: window.location.href,
          };

          getServices().analyticsClient.logger.report(
            "kernels",
            "user-code-error",
            loggedError
          );
        })
      );

      dispose.add(
        websocketDisconnectionSubscriber()((_: {}) => {
          getServices().analyticsClient.logger.report(
            "kernels",
            "websocket-disconnection"
          );
        })
      );

      currentJupyterFrame = jupyterFrame;
      return dispose;
    },
    navigate(jupyterUrl: string) {
      reportLifecycleChange(LifecycleStage.EDITOR_FRAME_REQUESTED, Date.now());
      const session = getSessionState(getStore().getState());
      const newSrc = `${jupyterUrl}&themeName=${session.themeName}&sourceType=${session.sourceType}`;
      const frame = ensureFrame();

      if (frame.src !== newSrc) {
        const parent = frame.parentNode!;
        // Remove the iframe from the DOM prior to navigation so the
        // src change doesn't affect browser history.
        parent.removeChild(frame);
        frame.src = newSrc;
        parent.appendChild(frame);
      }
    },
    setVisibility(isVisible) {
      if (isVisible) {
        reportLifecycleChange(LifecycleStage.EDITOR_FRAME_SHOWN, Date.now());
      }
      ensureFrame().style.visibility = isVisible ? "visible" : "hidden";
    },
    resize(dimensions) {
      const topChanged = dimensionsMemo.top !== dimensions.top;
      const widthChanged = dimensionsMemo.width !== dimensions.width;
      const heightChanged = dimensionsMemo.height !== dimensions.height;
      const shouldUpdate = topChanged || widthChanged || heightChanged;
      if (!shouldUpdate) {
        return;
      }

      dimensionsMemo = dimensions;

      if (!hasFrame()) {
        return;
      }

      const frame = ensureFrame();
      if (topChanged) {
        frame.style.top = `${dimensions.top}px`;
      }
      if (widthChanged) {
        frame.style.width = `${dimensions.width}px`;
      }
      if (heightChanged) {
        frame.style.height = `${dimensions.height}px`;
      }
    },
    getContentWindow() {
      const contentWindow = ensureFrame().contentWindow;
      if (!contentWindow) {
        throw new Error(
          "Attempt to access frame contentWindow before it exists."
        );
      }
      return contentWindow;
    },
    getElement() {
      return ensureFrame();
    },
    hasElement() {
      return hasFrame();
    },
    publishSessionState(session: SessionState) {
      if (hasFrame()) {
        publishSessionState(session);
      }
    },
    subscribeFrameFocus(subscriber: () => void) {
      focusSubscribers.push(subscriber);
      return () => {
        focusSubscribers.splice(focusSubscribers.indexOf(subscriber), 1);
      };
    },
  };
};
